import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import type { Theme } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";

import { partyShellPalette, type PartyShellPalette } from "../theme/theme";
import type { PartySavedRosterApi } from "./usePartySavedRoster";

function partyFromTheme(theme: Theme): PartyShellPalette {
  const p = (theme.palette as unknown as { party?: PartyShellPalette }).party;
  return p ?? partyShellPalette;
}

type Props = {
  api: PartySavedRosterApi;
  variant: "players" | "teams";
  /** Required when `variant` is `"players"`. */
  currentPlayers?: string[];
  onQuickAddPlayer?: (name: string) => void;
  maxPlayers?: number;
  onApplySavedTeams?: (teamA: string, teamB: string) => void;
};

export function PartySavedRosterSetupSection({
  api,
  variant,
  currentPlayers = [],
  onQuickAddPlayer,
  maxPlayers,
  onApplySavedTeams,
}: Props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const pal = partyFromTheme(theme);
  const ink = pal.ink;
  const inkDim = pal.inkDim;
  const inkFaint = pal.inkFaint;
  const hot = pal.hot;
  const roster = variant === "players" ? currentPlayers : [];
  const atCapacity = variant === "players" && maxPlayers != null && roster.length >= maxPlayers;
  const pickable = variant === "players" && onQuickAddPlayer ? api.savedPlayerNames.filter((n) => !roster.includes(n)) : [];
  const hasSavedTeams = Boolean(api.savedTeamA.trim() && api.savedTeamB.trim());
  const hasAnySaved =
    api.savedPlayerNames.length > 0 || api.savedTeamA.trim() || api.savedTeamB.trim();

  return (
    <Stack spacing={1.25} sx={{ mt: 1.5, mb: 0.5 }}>
      {variant === "players" && pickable.length > 0 && (
        <Box>
          <Typography sx={{ fontSize: 12, fontWeight: 800, color: inkDim, mb: 0.75 }}>
            {t("party.savedRoster.quickAddHeading")}
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.75}>
            {pickable.map((name) => (
              <Chip
                key={name}
                label={name}
                size="small"
                onClick={() => {
                  if (atCapacity || !onQuickAddPlayer) return;
                  onQuickAddPlayer(name);
                }}
                disabled={atCapacity}
                sx={{
                  fontWeight: 700,
                  borderColor: inkFaint,
                  color: ink,
                  bgcolor: "rgba(255,255,255,0.06)",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
                  "&.Mui-disabled": { opacity: 0.45 },
                }}
                variant="outlined"
              />
            ))}
          </Stack>
        </Box>
      )}

      {variant === "teams" && hasSavedTeams && onApplySavedTeams && (
        <Button
          type="button"
          size="small"
          variant="outlined"
          className="party-btn party-btn-paper"
          onClick={() => onApplySavedTeams(api.savedTeamA, api.savedTeamB)}
        >
          {t("party.savedRoster.useSavedTeams")}
        </Button>
      )}

      <FormControlLabel
        control={
          <Switch
            checked={api.rememberNames}
            onChange={(_, v) => api.setRememberNames(v)}
            size="small"
            sx={{
              "& .MuiSwitch-switchBase": { color: inkDim },
              "& .MuiSwitch-switchBase:hover": { bgcolor: "rgba(255,255,255,0.06)" },
              "& .MuiSwitch-switchBase.Mui-checked": { color: hot },
              "& .MuiSwitch-switchBase.Mui-checked:hover": {
                bgcolor: `color-mix(in oklab, ${hot} 18%, transparent)`,
              },
              "& .MuiSwitch-track": {
                opacity: 1,
                bgcolor: "rgba(255,255,255,0.22)",
              },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                bgcolor: `color-mix(in oklab, ${hot} 55%, transparent)`,
              },
            }}
          />
        }
        label={
          <Typography sx={{ fontSize: 13, color: ink, fontWeight: 600 }}>
            {t("party.savedRoster.rememberToggle")}
          </Typography>
        }
        sx={{ alignItems: "center", ml: 0, mr: 0, mt: 0.5 }}
      />
      <Typography sx={{ fontSize: 12, color: inkDim, lineHeight: 1.45 }}>
        {t("party.savedRoster.rememberHint")}
      </Typography>

      {hasAnySaved && (
        <Button
          type="button"
          size="small"
          onClick={api.clearSaved}
          sx={{
            alignSelf: "flex-start",
            fontSize: 12,
            color: inkDim,
            textDecoration: "underline",
            textUnderlineOffset: 3,
            "&:hover": { color: ink, bgcolor: "rgba(255,255,255,0.06)" },
          }}
        >
          {t("party.savedRoster.clearSaved")}
        </Button>
      )}
    </Stack>
  );
}
